import { lens, mergeDeepLeft, withLenses } from '@dhmk/zustand-lens';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { GAMMA_PRIME } from '@/constants/storage-flags';

import { createSelectors } from './common';
import {
  AchievementsNotificationModalsSlice,
  createAchievementsNotificationModalsSlice,
} from './slices/achievements-notification-modals';
import {
  CommunicationsShellSlice,
  createCommunicationsShellStore,
} from './slices/communications-shell-slice';
import { createEntityStore, EntitySlice } from './slices/entity-slice';
import { createEventCreateStore, EventCreateSlice } from './slices/event-create-slice';
import { createGlobalModalsSlice, GlobalModalsSlice } from './slices/global-modals-slice';
import { createLayoutStore, LayoutSlice } from './slices/layout-slice';
import { ProfileSlice, profileStore } from './slices/profile-slice';
import {
  createSelectedUserConfigStore,
  SelectedUserConfigSlice,
} from './slices/selected-user-config';
import { createSetupFundStore, SetupFundSlice } from './slices/setup-fund-slice';

type Store = {
  globalModalsSlice: GlobalModalsSlice;
  achievementsNotificationModalsSlice: AchievementsNotificationModalsSlice;
  entitySlice: EntitySlice;
  eventCreateSlice: EventCreateSlice;
  setupFundSlice: SetupFundSlice;
  profileSlice: ProfileSlice;
  selectedUserConfigSlice: SelectedUserConfigSlice;
  layoutSlice: LayoutSlice;
  communicationsShellSlice: CommunicationsShellSlice;
};
type PersistedKeys = (keyof typeof stores)[];

const stores = {
  globalModalsSlice: lens(createGlobalModalsSlice),
  achievementsNotificationModalsSlice: lens(createAchievementsNotificationModalsSlice),
  entitySlice: lens(createEntityStore),
  eventCreateSlice: lens(createEventCreateStore),
  setupFundSlice: lens(createSetupFundStore),
  profileSlice: lens(profileStore),
  selectedUserConfigSlice: lens(createSelectedUserConfigStore),
  layoutSlice: lens(createLayoutStore),
  communicationsShellSlice: lens(createCommunicationsShellStore),
} as const;

const persistedKeys: PersistedKeys = [
  'achievementsNotificationModalsSlice',
  'selectedUserConfigSlice',
  'layoutSlice',
];

export const useBoundStore = createSelectors(
  create<Store>()(
    persist(immer(withLenses(() => stores)), {
      name: GAMMA_PRIME,
      merge: (persistedState, currentState) => mergeDeepLeft(persistedState, currentState),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) =>
            persistedKeys.includes(key as PersistedKeys[number]),
          ),
        ),
    }),
  ),
);
